import axios from 'axios';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useConfig } from '../../../config';
import { useTranslation } from '../../../i18n/i18n';
import { Res, useApi } from '../../../state/api';
import { useSession } from '../../../state/session';
import { ApiCheckoutState, ApiDeliveryAddress, ApiDeliveryAddresses } from '../../../types';
import { ContentCard } from '../../ContentCard/ContentCard';
import { DisabledFormPart } from '../../DisabledFormPart/DisabledFormPart';
import { SimpleEditableCard } from '../../EditableCard/SimpleEditableCard';
import { AccountInfoBox } from '../../Form/AccountInfoBox';
import { AddressSummary } from '../../Form/AddressSummary';
import { Button } from '../../Form/Button';
import { CheckoutSelectDeliveryAddress } from '../../Form/CheckoutSelectDeliveryAddress';
import { DeliveryAddressInput } from '../../Form/DeliveryAddressInput';
import { useFormValidationContainer } from '../../Form/FormValidatorContainer';
import { Icon, close } from '../../Icon/Icon';
import { Modal } from '../../Modal/Modal';

interface DeliveryAddressSectionProps {
	checkoutState: ApiCheckoutState;
	updateCheckoutState: (state: ApiCheckoutState) => void;
	hasAppliedCheckoutStateData: boolean;
	focusEmailInput: () => void;
	deliveryFormOpen: boolean;
	setDeliveryFormOpen: (open: boolean) => void;
	setDeliveryAddressId: React.Dispatch<React.SetStateAction<number | undefined>>;
	deliveryValidationTrigger?: number;
}

type DeliveryAddressPayload = Omit<ApiDeliveryAddress, 'id' | 'primary' | 'lastUsed'>;
type PatchOp = { op: 'replace'; path: string; value: string | number };

const axiosConfig = { withCredentials: true };

export const DeliveryAddressSection = ({
	checkoutState,
	updateCheckoutState,
	hasAppliedCheckoutStateData,
	focusEmailInput,
	deliveryFormOpen,
	setDeliveryFormOpen,
	setDeliveryAddressId,
	deliveryValidationTrigger = 0,
}: DeliveryAddressSectionProps) => {
	const translations = useTranslation();
	const t = translations.checkout;
	const tForm = translations.form;
	const tGeneric = translations.generic;
	const { api } = useConfig();
	const { isLoggedIn, user } = useSession();
	const form = useFormValidationContainer();

	const addressFormRef = useRef<HTMLDivElement>(null);
	const validationTriggerRef = useRef(0);
	const [addressPickerOpen, setAddressPickerOpen] = useState(false);
	const [editingAddressId, setEditingAddressId] = useState<ApiDeliveryAddress['id']>();
	const [selectedAddress, setSelectedAddress] = useState('');
	const [storedAddressList, setStoredAddressList] = useState<ApiDeliveryAddress[]>([]);
	const [useCustomAddress, setUseCustomAddress] = useState(false);
	const [addressSetManually, setAddressSetManually] = useState(false);
	const [pendingValidationOpen, setPendingValidationOpen] = useState(false);
	const setManualSelection = (value: boolean) => setAddressSetManually(value);

	const hasStoredAddresses = user?.storedAddresses;
	const storedAddressState = useMemo(() => {
		const first = storedAddressList[0];
		if (!hasStoredAddresses || !first) {
			return {
				deliveryAddressId: undefined,
				selectedMatch: undefined,
				selectedStored: undefined,
				shouldShow: false,
				summaryAddress: checkoutState.delivery,
			};
		}
		const selectedMatch = storedAddressList.find(
			(address) => address.id !== undefined && String(address.id) === selectedAddress
		);
		const selectedStored = selectedMatch ?? first;
		const summaryAddress = useCustomAddress ? checkoutState.delivery : selectedStored;
		const shouldShow = hasStoredAddresses && storedAddressList.length > 0;
		const deliveryAddressId = !useCustomAddress ? selectedStored?.id ?? first?.id : undefined;

		return {
			deliveryAddressId,
			selectedMatch,
			selectedStored,
			shouldShow,
			summaryAddress,
		};
	}, [checkoutState.delivery, selectedAddress, storedAddressList, useCustomAddress]);

	const { data: deliveryAddresses, mutate: mutateDeliveryAddresses } = useApi(
		isLoggedIn ? Res.deliveryAddresses : null,
		{}
	);

	useEffect(() => {
		setDeliveryAddressId(
			storedAddressState.deliveryAddressId?.toString() ? Number(storedAddressState.deliveryAddressId) : undefined
		);
	}, [setDeliveryAddressId, storedAddressState.deliveryAddressId]);

	useEffect(() => {
		setStoredAddressList(deliveryAddresses ?? []);
		const first = deliveryAddresses?.[0];
		if (!hasStoredAddresses || !first || addressSetManually) return;
		setSelectedAddress(first?.id !== undefined ? String(first.id) : '');
		applyDeliveryAddress(first, false);
	}, [deliveryAddresses]);

	const handleDeliveryOpen = (open: boolean) => {
		setDeliveryFormOpen(open);
	};

	function buildPatch(original: DeliveryAddressPayload, changed: DeliveryAddressPayload): PatchOp[] {
		return (Object.keys(original) as (keyof DeliveryAddressPayload)[])
			.filter((key) => changed[key] !== original[key] && changed[key] !== undefined)
			.map((key) => ({
				op: 'replace',
				path: '/' + String(key),
				value: changed[key] as string | number,
			}));
	}

	function mapDeliveryAddressToPayload(address: DeliveryAddressPayload | ApiDeliveryAddress): DeliveryAddressPayload {
		return {
			company: address.company,
			careOf: address.careOf,
			firstname: address.firstname,
			lastname: address.lastname,
			street: address.street,
			doorCode: address.doorCode,
			city: address.city,
			zip: address.zip,
			phone: address.phone,
		};
	}

	async function handleSelectCustomAddress() {
		const isValid = form.validateDeliveryAddress({ deliveryType: checkoutState.delivery.type });
		if (!isValid) {
			if (addressFormRef.current) {
				addressFormRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
			}
			return;
		}
		const baseAddress =
			editingAddressId !== undefined
				? storedAddressList.find(
						(address) => address.id !== undefined && String(address.id) === String(editingAddressId)
					)
				: undefined;
		let updatedAddress: ApiDeliveryAddress | undefined;
		const payload: DeliveryAddressPayload = {
			company: form.deliveryCompany.value,
			careOf: form.deliveryCareOf.value,
			firstname: form.deliveryFirstname.value,
			lastname: form.deliveryLastname.value,
			street: form.deliveryStreet.value,
			doorCode: form.deliveryDoorCode.value,
			city: form.deliveryCity.value,
			zip: form.deliveryZip.value,
			phone: form.deliveryPhone.value,
		};
		if (baseAddress?.id !== undefined) {
			const originalPayload = mapDeliveryAddressToPayload(baseAddress);
			const patch = buildPatch(originalPayload, payload);
			updatedAddress = baseAddress;
			if (patch.length > 0) {
				try {
					await axios.patch(`${api}me/delivery-addresses/${baseAddress.id}.json`, patch, axiosConfig);
				} catch {
					return;
				}
			}
			updatedAddress = (await refreshStoredAddressesAndSelect(undefined, baseAddress.id)) ?? updatedAddress;
		} else {
			try {
				const { data } = await axios.post(`${api}me/delivery-addresses.json`, payload, axiosConfig);
				const insertedId = (data as { deliveryAddressId?: ApiDeliveryAddress['id'] })?.deliveryAddressId;
				if (insertedId !== undefined) {
					updatedAddress = await refreshStoredAddressesAndSelect(undefined, insertedId);
				}
			} catch {
				return;
			}
			if (!updatedAddress) {
				updatedAddress = await refreshStoredAddressesAndSelect(payload);
			}
		}
		if (updatedAddress) {
			applyDeliveryAddress(updatedAddress, true);
		} else {
			submitDeliveryAddress();
		}
		setUseCustomAddress(false);
		setEditingAddressId(undefined);
		setAddressPickerOpen(false);
		await mutateDeliveryAddresses();
	}

	const applyDeliveryAddress = useCallback(
		(address: ApiDeliveryAddress, selectedManually = true) => {
			if (address.id !== undefined) {
				setSelectedAddress(String(address.id));
				setEditingAddressId(address.id);
			}
			setManualSelection(selectedManually);
			form.fillAddressForm(address);
			if (!form.paymentCompany.value && address.company) {
				form.paymentCompany.setValueAndValidate(address.company);
			}
			if (!form.paymentCity.value && address.city) {
				form.paymentCity.setValueAndValidate(address.city);
			}
			if (!form.paymentStreet.value && address.street) {
				form.paymentStreet.setValueAndValidate(address.street);
			}
			if (!form.paymentZip.value && address.zip) {
				form.paymentZip.setValueAndValidate(address.zip);
			}
			const newDeliveryAddress: ApiCheckoutState['delivery'] = {
				...checkoutState.delivery,
				phone: address.phone,
				company: address.company,
				street: address.street,
				zip: address.zip,
				city: address.city,
				careOf: address.careOf,
				doorCode: address.doorCode,
				firstname: address.firstname,
				lastname: address.lastname,
			};
			updateCheckoutState({ ...checkoutState, delivery: newDeliveryAddress });
			setUseCustomAddress(false);
		},
		[checkoutState, form, setManualSelection]
	);

	function openEditAddress(address?: ApiDeliveryAddress) {
		if (address) {
			startEditingDeliveryAddress(address);
		} else {
			form.resetAddressForm();
			setEditingAddressId(undefined);
		}
		setAddressPickerOpen(true);
	}

	function openStoredAddressForValidation() {
		const addressToEdit = storedAddressState.selectedStored ?? storedAddressList[0];
		openEditAddress(addressToEdit);
		form.validateDeliveryAddress({ deliveryType: checkoutState.delivery.type });
	}

	useEffect(() => {
		if (!deliveryValidationTrigger || deliveryValidationTrigger === validationTriggerRef.current) return;
		validationTriggerRef.current = deliveryValidationTrigger;
		if (storedAddressState.shouldShow) {
			openStoredAddressForValidation();
		} else if (hasStoredAddresses) {
			setPendingValidationOpen(true);
		}
	}, [deliveryValidationTrigger, storedAddressState.shouldShow, hasStoredAddresses]);

	useEffect(() => {
		if (!pendingValidationOpen || !storedAddressState.shouldShow) return;
		setPendingValidationOpen(false);
		openStoredAddressForValidation();
	}, [pendingValidationOpen, storedAddressState.shouldShow]);

	async function refreshStoredAddressesAndSelect(
		match?: DeliveryAddressPayload,
		fallbackId?: ApiDeliveryAddress['id']
	): Promise<ApiDeliveryAddress | undefined> {
		try {
			const { data } = await axios.get<ApiDeliveryAddresses>(
				`${api}${Res.deliveryAddresses.uri({})}`,
				axiosConfig
			);
			const fetched = data ?? [];
			setStoredAddressList(fetched);
			if (match) {
				const found = fetched.find((addr) => {
					const payload = mapDeliveryAddressToPayload(addr);
					return (Object.keys(payload) as (keyof DeliveryAddressPayload)[]).every(
						(key) => payload[key] === match[key]
					);
				});
				if (found?.id !== undefined) return found;
			}
			if (fallbackId !== undefined) {
				const existing = fetched.find(
					(addr) => addr.id !== undefined && String(addr.id) === String(fallbackId)
				);
				if (existing?.id !== undefined) return existing;
			}
			if (fetched.length > 0) {
				return fetched[fetched.length - 1];
			}
		} catch {
			// ignore, keep local state
		}
		return undefined;
	}

	function startEditingDeliveryAddress(address: ApiDeliveryAddress) {
		form.fillAddressForm(address);
		setEditingAddressId(address.id);
		if (addressFormRef.current) {
			addressFormRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
		}
	}

	async function submitDeliveryAddress() {
		if (!hasAppliedCheckoutStateData) return;
		if (!form.paymentCompany.value && form.deliveryCompany.value) {
			form.paymentCompany.setValueAndValidate(form.deliveryCompany.value);
		}
		if (!form.paymentCity.value && form.deliveryCity.value) {
			form.paymentCity.setValueAndValidate(form.deliveryCity.value);
		}
		if (!form.paymentStreet.value && form.deliveryStreet.value) {
			form.paymentStreet.setValueAndValidate(form.deliveryStreet.value);
		}
		if (!form.paymentZip.value && form.deliveryZip.value) {
			form.paymentZip.setValueAndValidate(form.deliveryZip.value);
		}
		const newDeliveryAddress: ApiCheckoutState['delivery'] = {
			...checkoutState.delivery,
			phone: form.deliveryPhone.value,
			company: form.deliveryCompany.value,
			street: form.deliveryStreet.value,
			zip: form.deliveryZip.value,
			city: form.deliveryCity.value,
			careOf: form.deliveryCareOf.value,
			doorCode: form.deliveryDoorCode.value,
			firstname: form.deliveryFirstname.value,
			lastname: form.deliveryLastname.value,
		};
		updateCheckoutState({ ...checkoutState, delivery: newDeliveryAddress });
	}

	const isValidEmail = !!checkoutState.email;

	return (
		<>
			<DisabledFormPart isDisabled={!isValidEmail} onClick={focusEmailInput}>
				{storedAddressState.shouldShow && storedAddressState.summaryAddress ? (
					<ContentCard title={t.deliveryAddress}>
						{storedAddressList.length > 1 && (
							<div className="checkout-delivery-address-select">
								<CheckoutSelectDeliveryAddress
									selectedAddress={selectedAddress}
									deliveryAddresses={storedAddressList}
									onSelectAddress={(id) => {
										setSelectedAddress(id);
										const address = storedAddressList.find(
											(a) => a.id !== undefined && String(a.id) === id
										);
										if (address) {
											applyDeliveryAddress(address);
										}
									}}
								/>
							</div>
						)}
						<div className="checkout-delivery-address-summary">
							<AddressSummary address={storedAddressState.summaryAddress} />
							<Button
								type="button"
								className="secondary checkout-delivery-address-summary-button"
								onClick={() => openEditAddress(storedAddressState.selectedStored)}
							>
								{tGeneric.edit}
							</Button>
						</div>
						<div className="checkout-delivery-address-add-new">
							<Button type="button" className="secondary" onClick={() => openEditAddress(undefined)}>
								{tForm.addNewAddress}
							</Button>
						</div>
					</ContentCard>
				) : (
					<SimpleEditableCard
						title={t.deliveryAddress}
						onSubmit={submitDeliveryAddress}
						alwaysOpen
						renderWhenClosed={() => (
							<AccountInfoBox>
								<AddressSummary address={checkoutState.delivery} />
							</AccountInfoBox>
						)}
						renderWhenOpen={() => (
							<DeliveryAddressInput
								deliveryType={checkoutState.delivery.type}
								deliveryAddresses={storedAddressList}
								onBlur={submitDeliveryAddress}
								selectedAddress={selectedAddress}
								setSelectedAddress={setSelectedAddress}
							/>
						)}
						setOpen={handleDeliveryOpen}
						open={deliveryFormOpen}
						cardId="delivery-form"
					/>
				)}
			</DisabledFormPart>

			{storedAddressState.shouldShow && (
				<Modal
					className="content-card-modal checkout-delivery-address-modal"
					show={addressPickerOpen}
					disableBodyScroll
					onDismiss={() => {
						setAddressPickerOpen(false);
						setEditingAddressId(undefined);
					}}
					toggle={() => {
						setAddressPickerOpen((prev) => !prev);
						setEditingAddressId(undefined);
					}}
				>
					<ContentCard className="content checkout-delivery-address-modal-content">
						<div className="checkout-delivery-address-modal-header">
							<h2 className="checkout-delivery-address-modal-title">
								{editingAddressId ? tGeneric.edit : tForm.addNewAddress}
							</h2>

							<button
								className="close-btn"
								type="button"
								onClick={() => {
									setAddressPickerOpen(false);
									setEditingAddressId(undefined);
								}}
							>
								<Icon shape={{ ...close, fill: 'black' }} size={20} />
							</button>
						</div>
						<div className="modal-content">
							<div className="checkout-delivery-address-form" ref={addressFormRef}>
								<DeliveryAddressInput
									deliveryType={checkoutState.delivery.type}
									deliveryAddresses={[]}
									selectedAddress={selectedAddress}
									setSelectedAddress={setSelectedAddress}
								/>
								<div className="checkout-delivery-address-form-actions">
									<Button type="button" onClick={handleSelectCustomAddress}>
										{tGeneric.save}
									</Button>
								</div>
							</div>
						</div>
					</ContentCard>
				</Modal>
			)}
		</>
	);
};
